import React, { useEffect, useState, useMemo } from "react";
import {
  Trash2,
  Edit,
  Search,
  Table2,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faTimesCircle,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import Apiurl from "../../environment/Apiurl";

function PurchasePharmacy({ toggleSidebar }) {
  const navigate = useNavigate();
  const [responseData, setResponseData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showEntries, setShowEntries] = useState("10");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loadingPrivileges, setLoadingPrivileges] = useState(true);
  const [userPrivileges, setUserPrivileges] = useState({});

  // Fetch user privileges based on role_id
  const fetchUserPrivileges = async () => {
    try {
      setLoadingPrivileges(true);
      const userRoleId = localStorage.getItem("role_id") || "unknown";
      console.log("Fetching privileges for role_id:", userRoleId);

      if (userRoleId === "unknown") {
        console.warn("No role_id found in localStorage");
        setError(
          "No role assigned to the user. Please contact the administrator."
        );
        setTimeout(() => setError(null), 3000);
        return;
      }

      const response = await axios.get(`${Apiurl}/roles_and_privillage`);
      console.log(
        "Fetched roles data:",
        JSON.stringify(response.data, null, 2)
      );

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error(
          `Invalid or empty roles data received: ${JSON.stringify(
            response.data
          )}`
        );
      }

      const roleData = response.data.find(
        (role) => role.role_id === userRoleId
      );

      if (roleData) {
        console.log(
          "Found role data for role_id:",
          userRoleId,
          JSON.stringify(roleData, null, 2)
        );
        const privileges = {};
        roleData.pages.forEach((page) => {
          const pageName = page.page;
          const pagePrivileges = page.priviallages || page.privileges || {};
          privileges[pageName] = {
            access: page.access,
            ...pagePrivileges,
          };
          console.log(
            `Processing page: ${pageName}, Access: ${page.access}, Privileges:`,
            JSON.stringify(pagePrivileges, null, 2)
          );
        });
        console.log(
          "Set user privileges:",
          JSON.stringify(privileges, null, 2)
        );
        setUserPrivileges(privileges);
      } else {
        console.warn("No role data found for role_id:", userRoleId);
        setError("No role data found for the current user.");
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error(
        "Error fetching user privileges:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to fetch user privileges. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingPrivileges(false);
    }
  };

  // Check if user has specific permission for Purchase Order page
  const hasPermission = (permission) => {
    const purchasePrivileges = userPrivileges["Purchase List"];
    if (!purchasePrivileges) {
      console.log("No privileges found for Purchase Order page");
      return false;
    }
    console.log(
      `Checking permission '${permission}' for Purchase Order:`,
      JSON.stringify(purchasePrivileges, null, 2)
    );
    if (permission === "access") {
      const accessGranted = purchasePrivileges.access === true;
      console.log(`Access permission result: ${accessGranted}`);
      return accessGranted;
    }
    const hasPerm =
      purchasePrivileges.all === true ||
      purchasePrivileges[permission] === true;
    console.log(`Permission '${permission}' result: ${hasPerm}`);
    return hasPerm;
  };

  // Fetch purchase data
  const fetchData = async () => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for Purchase Order page");
      setError("You do not have permission to view purchase records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log(
        "Fetching purchase data from:",
        `${Apiurl}/purchase_pharmacy`
      );
      const response = await axios.get(`${Apiurl}/purchase_pharmacy`);
      console.log(
        "Fetched purchase data:",
        JSON.stringify(response.data, null, 2)
      );
      setResponseData(response.data.reverse() || []);
      setError(null);
    } catch (error) {
      console.error(
        "Error fetching purchase data:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to fetch purchase data. Please try again.");
      setTimeout(() => setError(null), 3000);
      setResponseData([]);
    } finally {
      setLoading(false);
    }
  };

  // Initialize privileges and data
  useEffect(() => {
    console.log("Fetching initial privileges...");
    fetchUserPrivileges();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!loadingPrivileges && hasPermission("access")) {
      console.log("Access granted for Purchase Order page");
      if (hasPermission("read")) {
        console.log("Fetching purchase data due to read permission");
        fetchData();
      } else {
        console.warn("Read permission denied for Purchase Order page");
        setError("You do not have permission to view purchase records.");
        setTimeout(() => setError(null), 3000);
      }
    } else if (!loadingPrivileges) {
      console.warn("Access permission denied for Purchase Order page");
      setError("You do not have permission to access this page.");
      setTimeout(() => setError(null), 3000);
    }
  }, [userPrivileges, loadingPrivileges]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    const result = responseData.filter((item) =>
      [
        item.invoice_number,
        item.purchase_date,
        item.supplier_id,
        item.net_total,
        item.supplier_details?.supplier_name,
        // item.medicine_details?.[0]?.medicine_name,
        item.list?.[0]?.expiry_date,
        item.list?.[0]?.mrp?.toString(),
        // item.list?.[0].medicine_details?.category,
      ].some((prop) =>
        prop != null
          ? prop.toString().toLowerCase().includes(searchTerm.toLowerCase())
          : false
      )
    );
    console.log("Filtered data:", result);
    return result;
  }, [responseData, searchTerm]);

  // Update items per page
  useEffect(() => {
    console.log("Updating items per page to:", showEntries);
    if (showEntries === "All") {
      setItemsPerPage(filteredData.length || 1);
    } else {
      setItemsPerPage(Number(showEntries));
    }
  }, [showEntries, filteredData]);

  // Reset to first page when search term or items per page changes
  useEffect(() => {
    console.log(
      "Resetting current page due to searchTerm or itemsPerPage change"
    );
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  // Calculate pagination values
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  console.log(
    "Pagination - totalPages:",
    totalPages,
    "itemsPerPage:",
    itemsPerPage,
    "filteredData.length:",
    filteredData.length
  );
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  console.log(
    "Current items:",
    currentItems,
    "from index",
    indexOfFirstItem,
    "to",
    indexOfLastItem
  );

  // Generate page list for pagination
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }
    console.log("Generated page list:", pages);
    return pages;
  };

  const pages = getPageList();

  const handlePageClick = (page) => {
    if (page !== "..." && page !== currentPage) {
      console.log("Navigating to page:", page);
      setCurrentPage(page);
    }
  };

  const handleAddPurchaseClick = () => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Purchase Order page");
      setError("You do not have permission to add purchase records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Navigating to Add Purchase Pharmacy page");
    toggleSidebar();
    navigate("/pharmacy/purchase-pharmacy/add-purchase-pharmacy");
  };

  const handleUpdatePurchaseClick = (purchase) => {
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Purchase Order page");
      setError("You do not have permission to update purchase records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log(
      "Navigating to Update Purchase Pharmacy page for ID:",
      purchase._id
    );
    toggleSidebar();
    navigate(
      `/pharmacy/purchase-pharmacy/update-purchase-pharmacy/${purchase._id}`,
      {
        state: { purchase },
      }
    );
  };

  const handleViewClick = (purchase) => {
    if (!hasPermission("download")) {
      console.warn("Download permission denied for Purchase Order page");
      setError("You do not have permission to view purchase records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log(
      "Navigating to View Purchase Pharmacy page for ID:",
      purchase._id
    );
    toggleSidebar();
    navigate(`/pharmacy/purchase-pharmacy/view/${purchase._id}`, {
      state: { purchase },
    });
  };

  const openDeleteModal = (id) => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Purchase Order page");
      setError("You do not have permission to delete purchase records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid purchase record ID:", id);
      setError("Invalid purchase record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Delete Purchase modal for ID:", id);
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    console.log("Closing Delete Purchase modal");
    setShowDeleteModal(false);
    setDeleteId(null);
    setError(null);
  };

  const confirmDelete = async () => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Purchase Order page");
      setError("You do not have permission to delete purchase records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!deleteId) {
      console.warn("Invalid purchase record ID:", deleteId);
      setError("Invalid purchase record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoadingDelete(true);
    try {
      console.log("Deleting purchase with ID:", deleteId);
      await axios.delete(`${Apiurl}/purchase_pharmacy/${deleteId}`);
      fetchData();
      closeDeleteModal();
    } catch (error) {
      console.error(
        "Error deleting purchase:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to delete purchase. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingDelete(false);
    }
  };

  // Determine payment status
  const getPaymentStatus = (item) => {
    if (item.paid === item.net_total) {
      return {
        label: "Paid",
        className: "bg-success text-white",
        icon: faCheckCircle,
      };
    } else if (item.paid > 0 && item.paid < item.net_total) {
      return {
        label: "Partial",
        className: "bg-warning text-dark",
        icon: faExclamationCircle,
      };
    } else {
      return {
        label: "Unpaid",
        className: "bg-danger text-white",
        icon: faTimesCircle,
      };
    }
  };

  if (loadingPrivileges) {
    return <div className="p-3 text-center">Loading permissions...</div>;
  }

  if (!hasPermission("access")) {
    console.log(
      "Access denied for role_id:",
      localStorage.getItem("role_id"),
      "on Purchase Order page"
    );
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          You don&apos;t have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3 px-2">
      <style>{`
        .btn-custom {
          background-color: #01AEBE;
          color: white;
          border: none;
        }
        .btn-custom:hover {
          background-color: #018A97;
          color: white;
        }
      `}</style>

      {hasPermission("create") && (
        <div className="d-flex justify-content-end align-items-center mb-3">
          <button
            className="btn btn-custom btn-sm"
            onClick={handleAddPurchaseClick}
          >
            <i className="fas fa-plus me-2"></i> Add Purchase
          </button>
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show mt-2">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {hasPermission("read") && (
        <div className="card card-body mt-2">
          <div className="row crm-table-toolbar">
            <div className="col-md-2">
              <div className="input-group">
                <span className="input-group-text">
                  <Table2 size={16} />
                </span>
                <select
                  className="form-select"
                  value={showEntries}
                  onChange={(e) => {
                    console.log(
                      "Changing entries per page to:",
                      e.target.value
                    );
                    setShowEntries(e.target.value);
                  }}
                >
                  <option value="All">Show All</option>
                  {[5, 10, 20, 50, 100, 200, 500, 1000].map((n) => (
                    <option key={n} value={n}>
                      Show {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-md-7"></div>
            <div className="col-md-3">
              <div className="d-flex align-items-center">
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control pe-5"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => {
                      console.log("Search term changed to:", e.target.value);
                      setSearchTerm(e.target.value);
                    }}
                    style={{ width: "250px", paddingRight: "2.5rem" }}
                  />
                  {searchTerm && (
                    <button
                      className="position-absolute bg-transparent border-0"
                      style={{
                        right: "30px",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                      onClick={() => setSearchTerm("")}
                    >
                      <i className="fas fa-times text-muted"></i>
                    </button>
                  )}
                  <Search
                    size={16}
                    className="position-absolute text-muted"
                    style={{
                      right: "10px",
                      top: "35%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {loading && <div className="text-center py-3">Loading...</div>}

          <div className="table-responsive mt-3">
            <table className="table table-hover">
              <thead style={{ backgroundColor: "#01AEBE", color: "white" }}>
                <tr>
                  <th>S.No</th>
                  <th>Purchase Date</th>
                  <th>Invoice No</th>
                  <th>Supplier Name</th>
                  <th>Amount (₹)</th>
                  <th>Days </th>
                  <th>Payment Status</th>
                  {(hasPermission("update") ||
                    hasPermission("delete") ||
                    hasPermission("download")) && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item, index) => {
                    const paymentStatus = getPaymentStatus(item);
                    return (
                      <tr key={item._id || index} className="align-middle">
                        <td>{indexOfFirstItem + index + 1}</td>
                        <td>{item.purchase_date || "N/A"}</td>
                        <td>{item.invoice_number || "N/A"}</td>
                        <td>{item.supplier_details?.supplier_name || "N/A"}</td>
                        <td>₹ {item.net_total || "N/A"}</td>
                        <td>{item.credit_days || "N/A"} Days</td>
                        {/* <td>
                          {item.list?.[0]?.mrp != null
                            ? `₹${Number(item.list[0].mrp).toFixed(2)}`
                            : "N/A"}
                        </td> */}
                        <td>
                          <span
                            className={`badge rounded-pill px-3 py-2 fw-semibold d-inline-flex align-items-center ${paymentStatus.className}`}
                          >
                            <FontAwesomeIcon
                              icon={paymentStatus.icon}
                              className="me-2"
                            />
                            {paymentStatus.label}
                          </span>
                        </td>
                        {(hasPermission("update") ||
                          hasPermission("delete") ||
                          hasPermission("download")) && (
                          <td>
                            {hasPermission("update") && (
                              <button
                                className="btn btn-sm p-1 border-0"
                                title="Edit"
                                onClick={() => handleUpdatePurchaseClick(item)}
                              >
                                <Edit size={16} className="text-primary" />
                              </button>
                            )}
                            {hasPermission("delete") && (
                              <button
                                className="btn btn-sm p-1 border-0"
                                title="Delete"
                                onClick={() => openDeleteModal(item._id)}
                                disabled={!item._id}
                              >
                                <Trash2 size={16} className="text-danger" />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={
                        hasPermission("update") ||
                        hasPermission("delete") ||
                        hasPermission("download")
                          ? 7
                          : 6
                      }
                      className="text-center py-4"
                    >
                      {loading ? (
                        "Loading..."
                      ) : (
                        <div className="text-muted">
                          <i className="fas fa-search fa-2x mb-2"></i>
                          <br />
                          No purchase records found matching your search
                          criteria
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredData.length > 0 && showEntries !== "All" && (
            <div className="container">
              <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() =>
                    currentPage > 1 && handlePageClick(currentPage - 1)
                  }
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} />
                  <span className="d-none d-sm-inline">Previous</span>
                </button>

                <div
                  className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                  style={{ maxWidth: "100%" }}
                >
                  {pages.map((page, index) => (
                    <button
                      key={index}
                      className={`btn btn-sm px-3 flex-shrink-0 ${
                        page === currentPage
                          ? "text-white"
                          : "btn-outline-secondary"
                      }`}
                      style={
                        page === currentPage
                          ? { backgroundColor: "#01AEBE", border: "none" }
                          : {}
                      }
                      onClick={() => handlePageClick(page)}
                      disabled={page === "..."}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() =>
                    currentPage < totalPages && handlePageClick(currentPage + 1)
                  }
                  disabled={currentPage === totalPages}
                >
                  <span className="d-none d-sm-inline">Next</span>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {hasPermission("delete") && (
        <div
          className={`modal fade ${showDeleteModal ? "show" : ""}`}
          style={{ display: showDeleteModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="deletePurchaseModalLabel"
          aria-hidden={!showDeleteModal}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeDeleteModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body text-center">
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      backgroundColor: "#fee2e2",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "40px",
                    }}
                  >
                    <AlertTriangle size={20} style={{ color: "#dc2626" }} />
                  </span>
                </div>
                <h5 className="modal-title" id="deletePurchaseModalLabel">
                  Delete Purchase
                </h5>
                <p style={{ marginTop: "10px" }}>
                  Are you sure you want to delete this purchase record?
                </p>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeDeleteModal}
                >
                  No, Keep it
                </button>
                <button
                  type="button"
                  className="btn btn-custom btn-sm"
                  onClick={confirmDelete}
                  disabled={loadingDelete}
                >
                  {loadingDelete ? "Deleting..." : "Yes, Delete It!"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && hasPermission("delete") && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
}

export default PurchasePharmacy;
